import { PayloadAction } from '@reduxjs/toolkit';
import { call, put } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { complianceAPI } from '@/api';
import type {
  BaseRequest,
  TransactionStatsResponse,
  TxListRequest,
  TxListResponse,
  TxRecheckRequest,
  TxResponse,
} from '@/api';
import { complianceTransferActions } from '@/redux/actions';
import { complianceTransferMappers } from '@/redux/mappers';
import { toast } from 'react-toastify';
import { COMMON_CONTAINER_ID } from '@/config';
import i18n from '@/i18n';
import { parseError } from '@/redux/helpers';

function* fetchTransferStats(action: PayloadAction<BaseRequest>): SagaIterator {
  try {
    const { payload } = action;
    const response: TransactionStatsResponse = yield call(
      complianceAPI.getTransactionStats,
      payload,
    );

    const data = complianceTransferMappers.fetchTransferStats(response);
    yield put(complianceTransferActions.setStats(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(complianceTransferActions.setLoading(false));
  }
}

function* fetchTransfers(action: PayloadAction<TxListRequest>): SagaIterator {
  try {
    const { payload } = action;
    const response: TxListResponse = yield call(complianceAPI.listTxs, payload);
    const data = complianceTransferMappers.fetchTransfers(response);
    yield put(complianceTransferActions.setList(data));
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(complianceTransferActions.setLoading(false));
  }
}

function* txRecheck(action: PayloadAction<TxRecheckRequest>): SagaIterator {
  try {
    const { payload } = action;
    const response: TxResponse = yield call(complianceAPI.txRecheck, payload);
    toast.success(i18n.t('translation.compliance_recheck_success'), {
      containerId: COMMON_CONTAINER_ID,
    });
  } catch (error: unknown) {
    const { message } = parseError(error);
    toast.error(message, { containerId: COMMON_CONTAINER_ID });
  } finally {
    yield put(complianceTransferActions.setLoading(false));
  }
}

function* applyDefault(): SagaIterator {
  try {
    yield call(complianceAPI.applyDefault);
    toast.success(i18n.t('translation.compliance_settings_default_success'), {
      containerId: COMMON_CONTAINER_ID,
    });
  } catch (error: unknown) {
    const { message } = parseError(error);
    toast.error(message, { containerId: COMMON_CONTAINER_ID });
  } finally {
    yield put(complianceTransferActions.setLoading(false));
  }
}

export { fetchTransferStats, fetchTransfers, txRecheck, applyDefault };
